import { createContext, useContext, useEffect, useMemo, useReducer, type ReactNode } from 'react';
import { onUnauthorized } from '../lib/api';
import { me, type CurrentUser } from './auth.api';
import { reduceCurrentUser, type CurrentUserState } from './current-user-state';

interface CurrentUserContextValue {
  state: CurrentUserState;
  setUser: (user: CurrentUser) => void;
  clearUser: () => void;
}

const CurrentUserContext = createContext<CurrentUserContextValue | null>(null);

/** Loads `me` once at app start, and drops to anonymous on any 401 so the shell bounces back to login instead of showing a broken screen. */
export function CurrentUserProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reduceCurrentUser, { status: 'loading' });

  useEffect(() => {
    me()
      .then((user) => dispatch({ type: 'loaded', user }))
      .catch(() => dispatch({ type: 'unauthenticated' }));
  }, []);

  useEffect(() => onUnauthorized(() => dispatch({ type: 'unauthenticated' })), []);

  const value = useMemo<CurrentUserContextValue>(
    () => ({
      state,
      setUser: (user) => dispatch({ type: 'loaded', user }),
      clearUser: () => dispatch({ type: 'unauthenticated' }),
    }),
    [state],
  );

  return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
}

export function useCurrentUser(): CurrentUserContextValue {
  const ctx = useContext(CurrentUserContext);
  if (!ctx) throw new Error('useCurrentUser must be used inside <CurrentUserProvider>');
  return ctx;
}
